import React, { useState } from 'react';
import axios from 'axios';
import { Form, Input, Button, Typography, Alert } from 'antd';
import { UserOutlined, LockOutlined, ThunderboltOutlined, SafetyOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const Login = ({ onLogin }) => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const onFinish = async ({ email, password }) => {
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/auth/login`, {
        email,
        password,
      });

      if (response.data.success) {
        localStorage.setItem('authToken', response.data.token);
        onLogin();
      } else {
        setError('Invalid credentials');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center p-4">
      <div className="w-full max-w-md relative z-10">
        
        {/* Glow Effects behind card */}
        <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-3xl blur opacity-20 animate-pulse-glow"></div>
        
        {/* Login Card */}
        <div className="glass-panel-dark rounded-3xl p-6 sm:p-8 relative">
          
          {/* Logo and Title */}
          <div className="text-center mb-6">
            <div className="relative inline-block mb-4 animate-float">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/40 border border-white/10">
                <ThunderboltOutlined className="text-white text-3xl" />
              </div>
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center border border-purple-500/50 shadow-[0_0_10px_rgba(168,85,247,0.5)]">
                <SafetyOutlined className="text-purple-400 text-sm" />
              </div>
            </div>
            <Title level={2} className="!mb-1 !text-2xl !font-bold !text-white flex items-center justify-center gap-2">
              Welcome Back
            </Title>
            <Text className="text-[10px] text-slate-400 tracking-wide uppercase font-medium">
              Access your next-gen compressor
            </Text>
          </div>

          <Form
            name="login"
            initialValues={{ remember: true }}
            onFinish={onFinish}
            layout="vertical"
            className="space-y-2"
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please input your email!' },
                { type: 'email', message: 'Please enter a valid email!' }
              ]}
              className="mb-4"
            >
              <Input
                prefix={<UserOutlined className="text-slate-400 text-base ml-1 mr-2 group-focus-within:text-purple-400 transition-colors" />}
                placeholder="Email address"
                size="large"
                className="h-12 !text-sm shadow-inner group"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                { required: true, message: 'Please input your password!' }
              ]}
              className="mb-6"
            >
              <Input.Password
                prefix={<LockOutlined className="text-slate-400 text-base ml-1 mr-2 group-focus-within:text-purple-400 transition-colors" />}
                placeholder="Password"
                size="large"
                className="h-12 !text-sm shadow-inner group"
              />
            </Form.Item>

            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                className="mb-6 rounded-xl animate-fade-in-up"
              />
            )}

            <Form.Item className="mb-0">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
                className="!h-10 rounded-xl font-bold text-base hover:scale-[1.02] active:scale-[0.98]"
              >
                {loading ? 'Authenticating...' : 'Secure Login'}
              </Button>
            </Form.Item>
          </Form>

          {/* Security Badge */}
          <div className="mt-6 pt-4 border-t border-slate-700/50 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-800/50 border border-slate-700 rounded-full">
              <SafetyOutlined className="text-purple-400" />
              <span className="text-slate-300 text-xs font-medium tracking-wide">Enterprise-Grade Encryption Active</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Login;